import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ENUM_LABELS, type ImportRunStatus, type ImportRunView, toOptions } from '#shared/types';
import { Badge, type BadgeTone } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { Select } from '#client/components/ui/Select';
import { useResourceList } from '#client/hooks/useResourceList';
import { useUrlState } from '#client/hooks/useUrlState';
import { formatCount, formatDateTime } from '#client/lib/format';
import { RESOURCE, routes } from '#client/lib/routes';
import { ButtonLink } from '#client/components/ui/ButtonLink';

const TONES: Record<ImportRunStatus, BadgeTone> = {
  staged: 'neutral',
  validating: 'info',
  validated: 'info',
  committing: 'info',
  committed: 'success',
  // Amber, not red. Rows genuinely landed, and treating it as a failure invites
  // exactly the re-run that would double them.
  partially_committed: 'warning',
  failed: 'critical',
};

const ENTITY_OPTIONS = toOptions(ENUM_LABELS.importEntity);

/** Static, so defined once rather than per render. */
const COLUMNS: Column<ImportRunView>[] = [
  {
    key: 'file',
    header: 'File',
    frozen: true,
    render: (run) => (
      <div>
        <strong>{run.file_name}</strong>
        <div className="c-muted">{ENUM_LABELS.importEntity[run.entity]}</div>
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '11rem',
    render: (run) => (
      <Badge tone={TONES[run.status]}>{ENUM_LABELS.importRunStatus[run.status]}</Badge>
    ),
  },
  {
    key: 'rows',
    header: 'Rows',
    align: 'end',
    width: '7rem',
    render: (run) => formatCount(run.total_rows),
  },
  {
    key: 'imported',
    header: 'Imported',
    align: 'end',
    width: '8rem',
    render: (run) =>
      run.failed_rows > 0 ? (
        <span>
          {formatCount(run.committed_rows)}{' '}
          <span className="c-import-failed">+{run.failed_rows} failed</span>
        </span>
      ) : (
        formatCount(run.committed_rows)
      ),
  },
  { key: 'by', header: 'By', width: '10rem', render: (run) => run.user_name },
  {
    key: 'when',
    header: 'Uploaded',
    width: '12rem',
    render: (run) => formatDateTime(run.created_at),
  },
];

/**
 * Every import ever run.
 *
 * The history is the point, not a by-product. "When did CBC go to Rs 900?" is a
 * question the lab will ask, and the answer is a run, its mapping and its date
 * — which is why the run row outlives the workbook it was made from.
 */
export function ImportListPage() {
  const navigate = useNavigate();
  const { params, setParam } = useUrlState();
  const entity = params.get('entity');

  const extraQuery = useMemo(() => (entity ? { entity } : {}), [entity]);
  const runs = useResourceList<ImportRunView>({
    path: RESOURCE.imports,
    extraQuery,
  });

  return (
    <Page
      title="Spreadsheet imports"
      subtitle="Read the lab’s existing workbooks into the system"
      primaryAction={
        <ButtonLink variant="primary" size="md" to={routes.importNew}>
          New import
        </ButtonLink>
      }
    >
      <Card flush>
        <div className="c-import-filter">
          <Select
            label="Kind"
            hideLabel
            options={[{ value: '', label: 'Everything' }, ...ENTITY_OPTIONS]}
            value={entity ?? ''}
            onChange={(choice) => setParam('entity', choice || undefined)}
          />
        </div>

        <DataTable
          columns={COLUMNS}
          rows={runs.items}
          rowKey={(run) => run.id}
          loading={runs.loading}
          skeletonRows={runs.limit}
          caption="Import history"
          onRowClick={(run) => void navigate(routes.importRun(run.id))}
          emptyState={
            <EmptyState
              title="Nothing imported yet"
              body="Upload a rate list, a reagent sheet or years of patient history."
            />
          }
        />

        <Pagination
          page={runs.page}
          limit={runs.limit}
          count={runs.count}
          onPageChange={runs.setPage}
        />
      </Card>
    </Page>
  );
}
